import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, AlertTriangle, Search, Loader2, CheckCircle, XCircle, Car, FileText, Gauge, Users, Camera, X, Star, Zap, Lock, Eye, Clock, DollarSign, MapPin, Printer, TrendingUp, Activity, ChevronRight, Award } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../utils/api';

function ScannerModal({ open, onClose, onScanResult }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const detectedRef = useRef(false);
  const [manualVin, setManualVin] = useState('');
  const [scanStatus, setScanStatus] = useState('starting');

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    if (!open) return;
    detectedRef.current = false;
    setScanStatus('starting');
    setManualVin('');
    const startScanner = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
        if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();
        setScanStatus('scanning');
        if ('BarcodeDetector' in window) {
          try {
            const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
            const vinFormats = ['code_128', 'code_39', 'code_93', 'qr_code', 'data_matrix', 'pdf417'].filter(f => supportedFormats.includes(f));
            if (vinFormats.length === 0) vinFormats.push('code_128', 'code_39', 'qr_code');
            const detector = new window.BarcodeDetector({ formats: vinFormats });
            const detectLoop = async () => {
              if (!mountedRef.current || detectedRef.current || !video.srcObject) return;
              try {
                if (video.readyState >= 2) {
                  const barcodes = await detector.detect(video);
                  if (barcodes.length > 0 && !detectedRef.current) {
                    const text = barcodes[0].rawValue?.trim();
                    if (text) {
                      const vinMatch = text.match(/[A-HJ-NPR-Z0-9]{17}/i);
                      if (vinMatch) { detectedRef.current = true; setScanStatus('detected'); onScanResult(vinMatch[0].toUpperCase()); stopCamera(); onClose(); return; }
                      else if (text.length >= 5) { detectedRef.current = true; onScanResult(text.toUpperCase()); stopCamera(); onClose(); return; }
                    }
                  }
                }
              } catch {}
              timerRef.current = setTimeout(detectLoop, 250);
            };
            setTimeout(() => { if (mountedRef.current && !detectedRef.current) detectLoop(); }, 800);
            return;
          } catch {}
        }
        try {
          const { Html5Qrcode } = await import('html5-qrcode');
          const hiddenDiv = document.createElement('div'); hiddenDiv.id = 'qr-fallback-region'; hiddenDiv.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;'; document.body.appendChild(hiddenDiv);
          const scanner = new Html5Qrcode('qr-fallback-region'); streamRef.current._fallbackScanner = scanner; streamRef.current._fallbackDiv = hiddenDiv;
          await scanner.start({ facingMode: "environment" }, { fps: 5, qrbox: { width: 300, height: 100 } }, (text) => {
            if (detectedRef.current) return;
            const vinMatch = text.match(/[A-HJ-NPR-Z0-9]{17}/i);
            if (vinMatch) { detectedRef.current = true; onScanResult(vinMatch[0].toUpperCase()); stopCamera(); onClose(); }
            else if (text.length >= 5) { detectedRef.current = true; onScanResult(text.toUpperCase()); stopCamera(); onClose(); }
          }, () => {});
        } catch { setScanStatus('error'); }
      } catch { if (mountedRef.current) { setScanStatus('error'); toast.error('Camera unavailable — enter VIN manually'); } }
    };
    startScanner();
    return () => { stopCamera(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopCamera = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { if (streamRef.current._fallbackScanner) { streamRef.current._fallbackScanner.stop().catch(() => {}); streamRef.current._fallbackDiv?.remove(); } streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
  };
  const handleClose = useCallback(() => { stopCamera(); onClose(); }, [onClose]);
  const handleManualSubmit = () => { const v = manualVin.trim().toUpperCase(); if (v.length >= 5) { detectedRef.current = true; onScanResult(v); stopCamera(); onClose(); } else toast.error('Enter at least 5 characters'); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="scanner-modal">
      <div className="flex items-center justify-between p-4 bg-black/80">
        <div className="flex items-center gap-2 text-white"><Camera className="w-5 h-5" /><h3 className="font-bold">Scan VIN</h3></div>
        <Button variant="ghost" size="icon" onClick={handleClose} className="rounded-xl text-white hover:bg-white/20" data-testid="close-scanner-btn"><X className="w-5 h-5" /></Button>
      </div>
      <div className="flex-1 relative flex items-center justify-center bg-black overflow-hidden">
        <video ref={videoRef} className="w-full h-full object-cover" autoPlay muted playsInline style={{ maxHeight: '70vh' }} />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><div className="relative border-2 border-white/60 rounded-lg" style={{ width: '85%', height: '25%', maxWidth: 420, maxHeight: 100 }}><div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400 rounded-tl-lg" /><div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400 rounded-tr-lg" /><div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400 rounded-bl-lg" /><div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400 rounded-br-lg" /></div></div>
        <div className="absolute left-[10%] right-[10%] h-0.5 bg-green-400 opacity-80" style={{ animation: 'scanLine 2s ease-in-out infinite', top: '45%' }} />
        <style>{`@keyframes scanLine { 0%, 100% { transform: translateY(-30px); } 50% { transform: translateY(30px); } }`}</style>
      </div>
      <div className="p-4 bg-black/90 space-y-3">
        <p className="text-white/80 text-sm text-center">{scanStatus === 'starting' ? 'Starting camera...' : scanStatus === 'scanning' ? 'Point at VIN barcode — hold steady' : scanStatus === 'error' ? 'Camera unavailable — type VIN below' : 'Scanning...'}</p>
        <div className="flex gap-2">
          <Input value={manualVin} onChange={(e) => setManualVin(e.target.value.toUpperCase())} placeholder="Or type VIN manually..." maxLength={17} className="flex-1 h-10 rounded-xl bg-white/10 border-white/20 text-white placeholder-white/40 font-mono tracking-wider" data-testid="manual-vin-input" onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()} />
          <Button onClick={handleManualSubmit} disabled={manualVin.length < 5} className="h-10 px-4 rounded-xl bg-green-500 text-white hover:bg-green-600 disabled:opacity-30" data-testid="manual-vin-submit">Enter</Button>
        </div>
      </div>
    </div>
  );
}

function ScoreBadge({ score, grade }) {
  const color = score >= 90 ? 'from-emerald-500 to-green-600' : score >= 75 ? 'from-cyan-500 to-teal-600' : score >= 60 ? 'from-amber-500 to-orange-600' : 'from-red-500 to-rose-600';
  const ringColor = score >= 90 ? '#10B981' : score >= 75 ? '#06B6D4' : score >= 60 ? '#F59E0B' : '#EF4444';
  const circumference = 2 * Math.PI * 52;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div className="flex flex-col items-center" data-testid="wakzy-score">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" stroke="#1e293b" strokeWidth="8" />
          <circle cx="60" cy="60" r="52" fill="none" stroke={ringColor} strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-black bg-gradient-to-b ${color} bg-clip-text text-transparent`}>{score}</span>
          <span className="text-xs font-bold text-gray-400">/ 100</span>
        </div>
      </div>
      <div className={`mt-2 px-4 py-1 rounded-full bg-gradient-to-r ${color} text-white text-sm font-bold`}>{grade} Grade</div>
    </div>
  );
}

function SummaryCheckItem({ icon: Icon, label, detail, status, iconBg }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${status === 'clear' ? 'border-green-100 bg-green-50/50' : status === 'warning' ? 'border-amber-100 bg-amber-50/50' : status === 'issue' ? 'border-red-100 bg-red-50/50' : 'border-gray-100 bg-gray-50/50'}`} data-testid={`check-${label.toLowerCase().replace(/[\s\/]/g, '-')}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg || 'bg-gray-100'}`}>
        <Icon className="w-5 h-5 text-gray-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {status === 'clear' && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
          {status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />}
          {status === 'issue' && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
          <span className="text-sm font-semibold text-gray-900">{label}</span>
        </div>
        {detail && <p className="text-xs text-gray-500 mt-0.5">{detail}</p>}
      </div>
    </div>
  );
}

function SafetyStars({ rating }) {
  const num = parseInt(rating);
  if (isNaN(num)) return <span className="text-gray-400 text-xs">Not Rated</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (<Star key={i} className={`w-4 h-4 ${i < num ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />))}
    </div>
  );
}

export default function VehicleHistory() {
  const [vin, setVin] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const vinRef = useRef('');

  const updateVin = useCallback((v) => { setVin(v); vinRef.current = v; }, []);

  const handleSearch = useCallback(async (searchVin) => {
    const v = searchVin || vinRef.current;
    if (v.length !== 17) { toast.error('VIN must be exactly 17 characters'); return; }
    setLoading(true); setData(null);
    try {
      const { data: result } = await api.get(`/api/vehicle-history/${v}`);
      setData(result);
      toast.success(`Wakzy Check report loaded`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to fetch vehicle history'); }
    finally { setLoading(false); }
  }, []);

  const handleScanResult = useCallback((scannedVin) => { setVin(scannedVin); vinRef.current = scannedVin; if (scannedVin.length === 17) handleSearch(scannedVin); }, [handleSearch]);
  const closeScanner = useCallback(() => setShowScanner(false), []);

  const handlePrint = () => { window.print(); };

  const card = data?.summary_card;
  const score = data?.score;

  return (
    <div data-testid="vehicle-history" className="print:p-0">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6 print:hidden">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
          <Shield className="w-7 h-7 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-gray-900 text-xl tracking-tight">Wakzy Check</h3>
          <p className="text-gray-500 text-sm">Premium Vehicle History Report — powered by NHTSA data</p>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-3 mb-6 print:hidden">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Enter VIN (17 characters)" value={vin} onChange={(e) => updateVin(e.target.value.toUpperCase())} maxLength={17} className="h-12 rounded-xl text-base font-mono tracking-wider pl-10 pr-16 border-2 focus:border-teal-500" data-testid="history-vin-input" onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{vin.length}/17</span>
        </div>
        <Button onClick={() => setShowScanner(true)} className="h-12 px-4 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-500/20" data-testid="open-scanner-btn"><Camera className="w-5 h-5" /></Button>
        <Button onClick={() => handleSearch()} disabled={loading || vin.length !== 17} className="h-12 px-8 rounded-xl bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold shadow-lg shadow-teal-500/20" data-testid="history-search-btn">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Run Report'}
        </Button>
      </div>

      <ScannerModal open={showScanner} onClose={closeScanner} onScanResult={handleScanResult} />

      {loading && (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mx-auto mb-4 animate-pulse shadow-xl shadow-teal-500/30">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <p className="font-semibold text-gray-700">Generating Wakzy Check Report...</p>
          <p className="text-sm text-gray-400 mt-1">Checking recalls, safety ratings, complaints & more</p>
        </div>
      )}

      {data && !loading && (
        <div className="space-y-6" data-testid="report-content">
          {/* Vehicle Hero + Score */}
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 text-white relative overflow-hidden" data-testid="report-vehicle-header">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full -mr-20 -mt-20" />
            <div className="relative flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold tracking-[0.2em] text-teal-400 uppercase">Wakzy Check Report</span>
                  <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded-full text-gray-400">{new Date(data.report_date).toLocaleDateString()}</span>
                </div>
                <h2 className="text-2xl font-bold tracking-tight">{data.vehicle}</h2>
                <p className="text-gray-400 font-mono text-sm mt-1">VIN: {data.vin}</p>
                <div className="flex flex-wrap gap-3 mt-4">
                  {data.specs?.engine && data.specs.engine !== 'N/A' && data.specs.engine !== 'Unknown' && <span className="text-xs bg-white/5 border border-white/10 px-3 py-1 rounded-lg">{data.specs.engine}</span>}
                  {data.specs?.transmission && data.specs.transmission !== 'N/A' && data.specs.transmission !== 'Unknown' && <span className="text-xs bg-white/5 border border-white/10 px-3 py-1 rounded-lg">{data.specs.transmission}</span>}
                  {data.specs?.drive_type && data.specs.drive_type !== 'N/A' && data.specs.drive_type !== 'Unknown' && <span className="text-xs bg-white/5 border border-white/10 px-3 py-1 rounded-lg">{data.specs.drive_type}</span>}
                  {data.specs?.fuel_type && data.specs.fuel_type !== 'N/A' && data.specs.fuel_type !== 'Unknown' && <span className="text-xs bg-white/5 border border-white/10 px-3 py-1 rounded-lg">{data.specs.fuel_type}</span>}
                  {data.specs?.trim && data.specs.trim !== 'N/A' && data.specs.trim !== 'Unknown' && <span className="text-xs bg-teal-500/20 border border-teal-500/30 px-3 py-1 rounded-lg text-teal-300">{data.specs.trim}</span>}
                </div>
              </div>
              {score && <ScoreBadge score={score.score} grade={score.grade} />}
            </div>
          </div>

          {/* Quick Checks Grid */}
          {card && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="quick-checks">
              <SummaryCheckItem icon={Car} label={card.accidents.label} detail={card.accidents.detail} status={card.accidents.status} iconBg="bg-blue-50" />
              <SummaryCheckItem icon={FileText} label={card.title_issues.label} detail={card.title_issues.detail} status={card.title_issues.status} iconBg="bg-purple-50" />
              <SummaryCheckItem icon={AlertTriangle} label={card.recalls.label} detail={card.recalls.detail} status={card.recalls.status} iconBg="bg-amber-50" />
              <SummaryCheckItem icon={Users} label={card.owners.label} detail={card.owners.detail} status={card.owners.status} iconBg="bg-teal-50" />
              <SummaryCheckItem icon={Car} label={card.usage_type.label} detail={card.usage_type.detail} status={card.usage_type.status} iconBg="bg-indigo-50" />
              <SummaryCheckItem icon={Award} label={card.safety_rating.label} detail={card.safety_rating.detail} status={card.safety_rating.status} iconBg="bg-yellow-50" />
            </div>
          )}

          {/* Market Value + Mileage + Ownership Row */}
          <div className="grid md:grid-cols-3 gap-4">
            {data.market_value && data.market_value.mid > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="market-value">
                <div className="flex items-center gap-2 mb-3"><DollarSign className="w-5 h-5 text-emerald-600" /><h4 className="font-bold text-gray-900 text-sm">Est. Market Value</h4></div>
                <div className="text-center">
                  <p className="text-3xl font-black text-emerald-600">${data.market_value.mid.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">${data.market_value.low.toLocaleString()} — ${data.market_value.high.toLocaleString()}</p>
                </div>
              </div>
            )}
            {data.mileage_estimate && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="mileage-estimate">
                <div className="flex items-center gap-2 mb-3"><Gauge className="w-5 h-5 text-blue-600" /><h4 className="font-bold text-gray-900 text-sm">Expected Mileage</h4></div>
                <div className="text-center">
                  <p className="text-3xl font-black text-blue-600">{data.mileage_estimate.expected.toLocaleString()}</p>
                  <p className="text-xs text-gray-400 mt-1">{data.mileage_estimate.low.toLocaleString()} — {data.mileage_estimate.high.toLocaleString()} mi range</p>
                </div>
              </div>
            )}
            {data.ownership && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="ownership-info">
                <div className="flex items-center gap-2 mb-3"><Users className="w-5 h-5 text-purple-600" /><h4 className="font-bold text-gray-900 text-sm">Ownership</h4></div>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-xs text-gray-500">Est. Owners</span><span className="text-sm font-bold">{data.ownership.estimated_owners}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">Usage</span><span className="text-sm font-bold">{data.ownership.usage_type}</span></div>
                  <div className="flex justify-between"><span className="text-xs text-gray-500">Vehicle Age</span><span className="text-sm font-bold">{data.ownership.vehicle_age_years} years</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Score Breakdown */}
          {score && score.reasons.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="score-breakdown">
              <div className="flex items-center gap-2 mb-3"><Activity className="w-5 h-5 text-teal-600" /><h4 className="font-bold text-gray-900 text-sm">Wakzy Score Breakdown</h4></div>
              <div className="space-y-1.5">
                {score.reasons.map((r, i) => (
                  <div key={i} className={`text-xs px-3 py-1.5 rounded-lg ${r.startsWith('+') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>{r}</div>
                ))}
              </div>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex gap-1 overflow-x-auto border-b border-gray-200 print:hidden">
            {[
              { id: 'overview', label: 'Safety & Specs', icon: Shield },
              { id: 'timeline', label: 'Timeline', icon: Clock },
              { id: 'recalls', label: `Recalls (${data.recalls_count})`, icon: AlertTriangle },
              { id: 'complaints', label: `Complaints (${data.complaints_count})`, icon: Gauge },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeSection === tab.id ? 'border-teal-500 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                data-testid={`report-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />{tab.label}
              </button>
            ))}
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={handlePrint} className="text-gray-400 hover:text-gray-600 print:hidden" data-testid="print-report-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
          </div>

          {/* Safety & Specs */}
          {activeSection === 'overview' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-teal-600" />Safety Ratings</h4>
                <div className="space-y-3">
                  {[{ label: 'Overall', value: data.safety_ratings?.overall }, { label: 'Frontal Crash', value: data.safety_ratings?.frontal_crash }, { label: 'Side Crash', value: data.safety_ratings?.side_crash }, { label: 'Rollover', value: data.safety_ratings?.rollover }].map(r => (
                    <div key={r.label} className="flex items-center justify-between"><span className="text-sm text-gray-600">{r.label}</span><SafetyStars rating={r.value} /></div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-teal-600" />Safety Equipment</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(data.safety_features || {}).map(([key, val]) => {
                    const positive = val && val !== 'N/A' && !['none', 'no', 'not available'].includes(String(val).toLowerCase());
                    return (
                    <div key={key} className="flex items-center gap-2 py-1.5">
                      {positive ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-gray-300 flex-shrink-0" />}
                      <span className="text-sm text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
                    </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:col-span-2">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-teal-600" />Vehicle Specifications</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(data.specs || {}).filter(([, v]) => v && v !== 'N/A').map(([key, val]) => (
                    <div key={key}><span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{key.replace(/_/g, ' ')}</span><p className="font-semibold text-gray-900 text-sm mt-0.5">{val}</p></div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          {activeSection === 'timeline' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="timeline-view">
              <h4 className="font-bold text-gray-900 mb-6 flex items-center gap-2"><Clock className="w-5 h-5 text-teal-600" />Vehicle History Timeline</h4>
              {(!data.timeline || data.timeline.length === 0) ? (
                <div className="text-center py-8 text-gray-400"><CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" /><p className="font-medium">Clean History</p><p className="text-sm">No significant events found</p></div>
              ) : (
                <div className="relative">
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
                  <div className="space-y-4">
                    {data.timeline.map((event, i) => (
                      <div key={i} className="relative flex gap-4 pl-3" data-testid={`timeline-${i}`}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${event.severity === 'danger' ? 'bg-red-500' : event.severity === 'warning' ? 'bg-amber-500' : 'bg-teal-500'}`}>
                          {event.type === 'recall' ? <AlertTriangle className="w-3.5 h-3.5 text-white" /> : event.type === 'complaint' ? <XCircle className="w-3.5 h-3.5 text-white" /> : <CheckCircle className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className={`flex-1 p-3 rounded-xl border ${event.severity === 'danger' ? 'bg-red-50 border-red-100' : event.severity === 'warning' ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-semibold text-gray-900">{event.title}</span>
                            <span className="text-[10px] text-gray-400">{event.date}</span>
                          </div>
                          <p className="text-xs text-gray-600">{event.description}</p>
                          {event.tags?.length > 0 && <div className="flex gap-1 mt-2">{event.tags.map((t, j) => <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">{t}</span>)}</div>}
                          {event.campaign && <span className="text-[10px] font-mono text-gray-400 mt-1 block">Campaign: {event.campaign}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Recalls */}
          {activeSection === 'recalls' && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-rose-500 flex items-center justify-between"><span className="font-bold text-white flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Safety Recalls ({data.recalls_count})</span></div>
              <div className="p-5">
                {data.recalls.length === 0 ? (<div className="text-center py-8 text-gray-400"><CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" /><p className="font-medium">No Recalls Found</p></div>
                ) : (
                  <div className="space-y-4">{data.recalls.map((recall, i) => (
                    <div key={i} className="p-4 bg-red-50 rounded-xl border border-red-100" data-testid={`recall-${i}`}>
                      <div className="flex items-start justify-between mb-2"><span className="text-xs font-mono bg-red-100 text-red-700 px-2 py-0.5 rounded">{recall.campaign_number}</span><span className="text-xs text-gray-500">{recall.report_date}</span></div>
                      <p className="font-medium text-gray-900 text-sm">{recall.component}</p>
                      <p className="text-sm text-gray-600 mt-1">{recall.summary}</p>
                      {recall.consequence && <p className="text-xs text-red-600 mt-2 bg-red-100 p-2 rounded-lg"><strong>Risk:</strong> {recall.consequence}</p>}
                      {recall.remedy && <p className="text-xs text-green-700 mt-1 bg-green-50 p-2 rounded-lg"><strong>Fix:</strong> {recall.remedy}</p>}
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}

          {/* Complaints */}
          {activeSection === 'complaints' && (
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-between">
                <span className="font-bold text-white flex items-center gap-2"><Gauge className="w-5 h-5" />Consumer Complaints ({data.complaints_count})</span>
                {(data.crash_count > 0 || data.fire_count > 0) && (
                  <div className="flex gap-2">{data.crash_count > 0 && <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded">{data.crash_count} Crashes</span>}{data.fire_count > 0 && <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded">{data.fire_count} Fires</span>}</div>
                )}
              </div>
              <div className="p-5">
                {data.complaints.length === 0 ? (<div className="text-center py-8 text-gray-400"><CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-400" /><p className="font-medium">No Complaints Found</p></div>
                ) : (
                  <div className="space-y-3">{data.complaints.map((c, i) => (
                    <div key={i} className="p-4 bg-amber-50 rounded-xl border border-amber-100" data-testid={`complaint-${i}`}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">{c.component}</span>
                        {c.crash && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Crash</span>}
                        {c.fire && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">Fire</span>}
                        {c.injuries > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">{c.injuries} Injuries</span>}
                        {c.mileage && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{Number(c.mileage).toLocaleString()} mi</span>}
                      </div>
                      <p className="text-sm text-gray-600">{c.summary}</p>
                      {c.date && <p className="text-xs text-gray-400 mt-1">{c.date}</p>}
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center py-4 text-xs text-gray-400 border-t border-gray-100" data-testid="report-footer">
            <p>Wakzy Check Report — Generated {new Date(data.report_date).toLocaleString()}</p>
            <p className="mt-1">Data sourced from NHTSA. Market values and ownership estimates are approximations. For official records, consult your state DMV.</p>
          </div>
        </div>
      )}
    </div>
  );
}
